import {
  ApolloLink,
  type FetchResult,
  type NextLink,
  Observable,
  type Observer,
  type Operation,
} from "@apollo/client";
import type { GraphQLError } from "graphql";

type GraphqlResult<T> = GraphQLError | T;

type RetryFunction<T> = (
  count: number,
  operation: Operation,
  value: GraphqlResult<T> | undefined,
  error: Error | undefined,
) => boolean;

type DelayFunction<T> = (
  count: number,
  operation: Operation,
  value: GraphqlResult<T> | undefined,
  error: Error | undefined,
) => number;

function RetryableOperation<T>(
  operation: Operation,
  nextLink: NextLink,
  delayFor: DelayFunction<T>,
  retryIf: RetryFunction<T>,
) {
  const values = [];
  const observers = [];
  let currentSubscription: ReturnType<
    Observable<FetchResult>["subscribe"]
  > | null = null;
  let complete = false;
  let error: unknown = null;
  let timerId: NodeJS.Timeout = null;
  let retryCount = 0;

  function scheduleRetry(delay) {
    timerId = setTimeout(() => {
      timerId = null;
      // This is hoisted

      newTry();
    }, delay);
  }

  function onNext(value: T) {
    const shouldRetry = retryIf(retryCount + 1, operation, value, undefined);

    if (shouldRetry) {
      retryCount += 1;
      scheduleRetry(delayFor(retryCount, operation, value, undefined));
      return;
    }

    values.push(value);
    observers.forEach((observer) => {
      if (observer) {
        observer.next(value);
      }
    });
  }

  async function onError(err: Error) {
    // Should we retry?
    const shouldRetry = await retryIf(
      retryCount + 1,
      operation,
      undefined,
      err,
    );

    if (shouldRetry) {
      retryCount += 1;
      scheduleRetry(delayFor(retryCount, operation, undefined, err));
      return;
    }

    error = err;
    observers.forEach((observer) => {
      if (observer) {
        observer.error(err);
      }
    });
  }

  function onComplete() {
    // Do not complete if there is a retry active
    if (!timerId) {
      complete = true;
      observers.forEach((observer) => {
        if (observer) {
          observer.complete();
        }
      });
    }
  }

  function newTry() {
    currentSubscription = nextLink(operation).subscribe({
      next: onNext,
      error: onError,
      complete: onComplete,
    });
  }

  function start() {
    if (currentSubscription) return; // Already started.

    newTry();
  }

  function subscribe(observer: Observer<GraphqlResult<T>>) {
    observers.push(observer);
    // There may be already values in the buffer, must push all to the subscription.
    values.forEach(observer.next);

    if (complete) {
      observer.complete();
    } else if (error) {
      observer.error(error);
    }
  }

  function cancel() {
    if (currentSubscription) {
      currentSubscription.unsubscribe();
    }
    clearTimeout(timerId);
    timerId = null;
    currentSubscription = null;
  }

  function unsubscribe(observer: Observer<GraphqlResult<T>>) {
    const index = observers.indexOf(observer);
    if (index >= 0) {
      observers[index] = null;
    }

    // No observers left, stop
    if (observers.every((o) => o === null)) {
      cancel();
    }
  }

  return {
    start,
    subscribe,
    unsubscribe,
  };
}

export interface ElephantRetryLinkArgs {
  calculateDelay: DelayFunction<unknown>;
  shouldRetry: RetryFunction<unknown>;
}

export class ElephantRetryLink extends ApolloLink {
  private delayFor: DelayFunction<unknown>;
  private retryIf: RetryFunction<unknown>;

  constructor(args: ElephantRetryLinkArgs) {
    super();

    this.delayFor = args.calculateDelay;
    this.retryIf = args.shouldRetry;
  }

  public request(
    operation: Operation,
    nextLink: NextLink,
  ): Observable<FetchResult> {
    const retryable = RetryableOperation(
      operation,
      nextLink,
      this.delayFor,
      this.retryIf,
    );
    retryable.start();

    return new Observable((observer) => {
      retryable.subscribe(observer);
      return () => {
        retryable.unsubscribe(observer);
      };
    });
  }
}
